// Brand identity of the current host (configuration-driven white label). Fetched from /api/v1/brand,
// cached for offline starts, and applied to title, manifest, icons and theme defaults. Demo mode keeps the
// reference identity ("TaskCool", design v7) for visual parity.
import { reactive } from 'vue';
import { openDB } from 'idb';

export interface BrandInfo {
  display_name: string; short_name: string; accent: string; theme: string; lang: string; signups: boolean;
  /** 8.6 admin console: sign-in/registration refused for non-staff while true. */
  maintenance?: boolean; signup_mode?: 'open' | 'invite' | 'closed';
  support_email: string | null; support_url: string | null; terms_url: string | null; privacy_url: string | null;
  icons: { icon192: string | null; icon512: string | null; favicon: string | null; logo: string | null };
}

export const brand = reactive<BrandInfo>({ display_name: 'TaskCool', short_name: 'TaskCool', accent: '#5b8def', theme: 'dark', lang: 'en', signups: true, support_email: null, support_url: null, terms_url: null, privacy_url: null, icons: { icon192: null, icon512: null, favicon: null, logo: null } });

const db = () => openDB('taskpanes|brand|' + encodeURIComponent(location.origin), 1, { upgrade(d) { d.createObjectStore('kv'); } });

function apply(): void {
  document.title = brand.display_name;
  const set = (rel: string, href: string | null) => {
    if (!href) return;
    let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
    if (!el) { el = document.createElement('link'); el.rel = rel; document.head.appendChild(el); }
    el.href = href;
  };
  set('icon', brand.icons.favicon || brand.icons.icon192);
  set('apple-touch-icon', brand.icons.icon192);
  set('manifest', '/manifest.webmanifest');
  const meta = document.querySelector('meta[name="apple-mobile-web-app-title"]') || Object.assign(document.createElement('meta'), { name: 'apple-mobile-web-app-title' });
  meta.setAttribute('content', brand.short_name);
  if (!meta.parentNode) document.head.appendChild(meta);
}

/** Loads the brand identity (network first, cached copy when offline). */
/** Set when the server answers 421: this host is not an active verified domain of any brand (fail closed). */
export const hostStatus = { notServed: false };

export async function loadBrand(): Promise<BrandInfo> {
  try {
    const r = await fetch('/api/v1/brand', { credentials: 'same-origin', cache: 'no-store' });
    hostStatus.notServed = r.status === 421;
    if (r.ok) { Object.assign(brand, await r.json()); try { await (await db()).put('kv', JSON.parse(JSON.stringify(brand)), 'brand'); } catch { /* storage unavailable */ } }
  } catch {
    try { const cached = await (await db()).get('kv', 'brand'); if (cached) Object.assign(brand, cached); } catch { /* none cached */ }
  }
  apply();
  return brand;
}
